/**
 * Sync-status pill (§13.2): "a slim sync-status pill (last sync time, spinner
 * while running) lives in the top bar."
 *
 * It reports what is true and never an invented fourth state: syncing, when the
 * last run finished, or an honest "no inbox". A *failed* run is called out here
 * in colour rather than left to the Settings page, because a sync that stopped
 * working has no other symptom — statements simply stop arriving, and a missing
 * thing is invisible (§7.7).
 */

import { useIsMutating, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, MailWarning, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

import { PulseDot } from "@/components/ui/motion";
import { syncApi } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDateTime, formatRelativeTime } from "@/lib/format";

const BASE =
  "inline-flex min-w-0 max-w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs max-md:min-h-10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50";

export function SyncPill() {
  const { data } = useQuery({
    queryKey: ["sync", "status"],
    queryFn: syncApi.status,
    refetchInterval: 60_000,
  });
  const syncing = useIsMutating({ mutationKey: ["sync", "run"] }) > 0;

  if (!data) return null;

  if (data.mode === "disabled" || !data.configured) {
    return (
      <Link
        to="/settings"
        className={cn(BASE, "border-hairline bg-raised text-faint hover:text-muted")}
        title={
          data.mode === "disabled"
            ? "No inbox connected. Statements can still be uploaded by hand. Open Settings to connect one."
            : (data.detail ?? "The inbox needs attention. Open Settings.")
        }
      >
        <MailWarning className="h-3 w-3" aria-hidden />
        {data.mode === "disabled" ? "No inbox" : "Inbox needs attention"}
      </Link>
    );
  }

  if (syncing) {
    return (
      <span className={cn(BASE, "border-hairline bg-raised text-muted")} aria-live="polite">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Syncing…
      </span>
    );
  }

  const run = data.last_run;
  const failed = run != null && !run.ok;
  const when = run ? (run.finished_at ?? run.started_at) : null;

  return (
    <Link
      to="/settings"
      className={cn(
        BASE,
        failed
          ? "border-overdue/25 bg-overdue/10 text-overdue hover:bg-overdue/15"
          : "border-hairline bg-raised/60 text-muted hover:text-ink",
      )}
      title={
        failed
          ? `Last sync failed: ${run?.error ?? "unknown error"}`
          : run
            ? `Last sync ${formatDateTime(when)}`
            : "Connected. No sync has run yet."
      }
    >
      {failed ? (
        <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
      ) : run ? (
        <PulseDot tone="paid" className="mx-0.5" />
      ) : (
        <RefreshCw className="h-3 w-3 shrink-0" aria-hidden />
      )}
      <span className="truncate">
        {failed ? "Sync failed" : run ? `Synced ${formatRelativeTime(when)}` : "Never synced"}
      </span>
    </Link>
  );
}
